import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import classNames from "classnames/bind";
import Logo from "@/assets/svg/logo.svg";
import { Container, ReduceMotionToggle, RhLink, SearchTray } from "@components";
import { AcfMediaFragment, GetPageWrapperDataQuery } from "@graphqlTypes";
import { useSearch } from "@/providers/SearchContext";
import { useHeader } from "@/providers/HeaderContext";
import { useScroll } from "@/providers/ScrollContext";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import DesktopSideNav from "./Components/DesktopSideNav/DesktopSideNav";
import MobileSideNav from "./Components/MobileSideNav/MobileSideNav";
import DesktopMenu from "./Components/DesktopMenu/DesktopMenu";
import { easeOutQuad } from "@/core/easing";

import { AnimVals } from '@/utils/animUtils';

import styles from "./Header.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = classNames.bind(styles);

export type TopLevelNavigation = SiteNavigation["navigationSettingsFields"]["topLevelNavigation"];
type HeaderProps = SiteNavigation & {
  languages: GetPageWrapperDataQuery["languages"];
  disableHideOnScroll?: boolean;
};

export default function Header(props: HeaderProps) {
  const { scrollY, lastScrollY, setLastScrollY } = useScroll();
  const { setShowSearchTray, showSearchTray } = useSearch();
  const [lastHidePosY, setLastHidePosY] = useState(0);
  const { labels } = usePageWrapperData();
  const { highlightColor } = usePageHighlight();
  const [style, setStyle] = useState<React.CSSProperties>({});
  const [showBg, setShowBg] = useState(false);
  const [bgHeight, setBgHeight] = useState(0);

  const {
    isScrolled,
    setIsScrolled,
    scrollDirection,
    isHeaderFixed,
    setScrollDirection,
    expandedMenu,
    setHeaderRef,
    ignoreScroll,
    dropdownExpanded,
    menuExpanded,
  } = useHeader();

  const disableHideOnScroll = props.disableHideOnScroll ?? false;
  const handleSearchClick = () => setShowSearchTray(!showSearchTray);
  const hideSearchTray = () => setShowSearchTray(false);

  // Hold the "menu open" state briefly after it goes false. Hovering BETWEEN
  // dropdown items collapses one dropdown a beat before the next expands (the
  // 150ms mouse-enter delay in HeaderDropdown), so without this the black nav
  // bar drops for that gap and the transparent exhibit nav flashes back in —
  // the recurring "nav flickers black <-> transparent on hover" regression. The
  // grace (> the enter delay) keeps menu-expanded continuous across the swap, so
  // the riding rule's transparent snap can't fire mid-hover. Guarded by the
  // qa/digital-exhibit/nav-hover-verify.js test — run it after any hero/nav edit.
  const menuOpen = dropdownExpanded || menuExpanded || showSearchTray;
  const [menuOpenSticky, setMenuOpenSticky] = useState(false);
  useEffect(() => {
    if (menuOpen) {
      setMenuOpenSticky(true);
      return;
    }
    const id = setTimeout(() => setMenuOpenSticky(false), 300);
    return () => clearTimeout(id);
  }, [menuOpen]);

  useEffect(() => {
    // Update scrolled state based on scroll position
    if (scrollY > 10 && !isScrolled) {
      setIsScrolled(true);
    } else if (scrollY < 5 && isScrolled) {
      setIsScrolled(false);
    }

    // Determine scroll direction
    if (!ignoreScroll && !dropdownExpanded && !menuExpanded && !disableHideOnScroll) {
      if (scrollY > document.documentElement.scrollHeight - window.innerHeight - 300) { // show header at bottom of screen to prevent hide/show bounciness on mobile
        setScrollDirection("up");
      } else {
        if (scrollY > lastScrollY) {
          setScrollDirection("down");
          setLastHidePosY(scrollY);
        } else if (scrollY - lastHidePosY < -80 || scrollY === 0) {
          setScrollDirection("up");
        }
      }
    } else if (disableHideOnScroll) {
      // Force header visible when hide-on-scroll is disabled
      setScrollDirection("up");
    }
    setLastScrollY(scrollY);
  }, [scrollY]);

  useEffect(() => {
    setStyle({ "--highlight-color": highlightColor } as React.CSSProperties);
  }, [highlightColor]);

  useEffect(() => {
    if (!expandedMenu) {
      setShowBg(false);
    } else {
      setBgHeight(expandedMenu.clientHeight);
      setShowBg(true);
    }
  }, [expandedMenu]);
  if (!props?.navigationSettingsFields) return;
  const { navigationSettingsFields, languages } = props;

  // -------------------- Animation
  const isRevealEnabled = true; // May be useful later -Gabe
  const isInView = true;        // May be useful later -Gabe
  const REVEAL = AnimVals;
  const createRevealMotionProps = (delay: number = 0) => ({
    initial: isRevealEnabled ? {
      opacity: REVEAL.opacity,
    } : {},
    animate: isInView ? {
      opacity: 1,
    } : {},
    transition: {
      duration: REVEAL.lg.duration,
      ease: REVEAL.ease,
      delay
    }
  });
  // -------------------- /Animation

  return (
    <>
      <a className={cx("skip-to-content-link")} href="#main" data-gtm-section="nav"><span className={cx("cta")}>{labels?.skipToContent}</span></a>
      <ReduceMotionToggle className={cx("reduce-motion-toggle")} dataGtmSection="nav" />
      <motion.header
        className={cx("header", {
          scrolled: isScrolled || disableHideOnScroll,
          "menu-expanded": menuOpenSticky,
          "hide-header": scrollDirection === "down" && !isHeaderFixed,
          "show-header": scrollDirection === "up",
        })}
        style={style}
        ref={setHeaderRef}
        {...createRevealMotionProps(0.5)}
      >
        <AnimatePresence>
          { showBg && (
            <motion.div
              className={cx("header__background")}
              initial={{ height: bgHeight, opacity: 0 }}
              animate={{ height: bgHeight, opacity: 1, transition: { duration: 0.35, ease: easeOutQuad } }}
              exit={{ height: bgHeight, opacity: 0, transition: { duration: 0.2, delay: 0.15 } }}
            />
          )}
        </AnimatePresence>
        <Container className={cx(["header__container"])} fullWidth={true}>
          <div className={cx("header__navbar")}>
            <RhLink aria-label="Rock & Roll Hall of Fame logo, home" href="/" data-gtm-section="nav">
              <Logo
                alt="Rock & Roll Hall of Fame logo, home"
                className={cx("header__logo")}
              />
            </RhLink>
            <DesktopMenu {...navigationSettingsFields} hideSearchTray={hideSearchTray} onSearchClick={handleSearchClick} />
            <DesktopSideNav {...navigationSettingsFields} languages={languages} />
            <MobileSideNav {...navigationSettingsFields} onSearchClick={handleSearchClick} languages={languages} />
          </div>
        </Container>
        <SearchTray {...navigationSettingsFields} />
      </motion.header>
    </>
  );
}
